import React, { useState } from 'react';
import { useToasts } from 'Context';
import { ConfirmDialog } from 'v3/Components/Shared/ConfirmDialog';
import { DateTimePickerInput } from 'v3/Components/Form/DateTimePickers/DateTimePicker';
import TextArea from 'v3/Components/Form/TextArea';
import { ReviewerLifecycleStatus } from 'v3/Models/Enums';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { buildEndpoint, useDataPoster } from 'Utilities';
import './ReviewerProfile.NotReadyDialog.scss';

type ReviewerProfileNotReadyDialogProps = {
  reviewerId: string;
  dialogVisible: boolean;
  toggleDialog: () => void;
  onSuccess: () => void;
};

const ReviewerProfileNotReadyDialog: React.FC<ReviewerProfileNotReadyDialogProps> = ({
  reviewerId,
  dialogVisible,
  toggleDialog,
  onSuccess,
}) => {
  const toast = useToasts();
  const [followupDate, setFollowupDate] = useState('');
  const [followupDateError, setFollowupDateError] = useState<string | undefined>();
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [postData] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'status'),
    undefined,
    true,
    '3.0'
  );

  const handleClose = () => {
    setFollowupDate('');
    setFollowupDateError(undefined);
    setNote('');
    setNoteError(undefined);
    toggleDialog();
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setFollowupDateError(undefined);
    setNoteError(undefined);
    try {
      const resp: any = await postData({
        newStatus: { value: ReviewerLifecycleStatus.NotReady },
        followupDate,
        note,
      });
      if (resp?.status === 200) {
        toast.success('Reviewer has been marked as Not Ready');
        onSuccess();
        handleClose();
      } else if (resp?.response?.status === 400) {
        const data = resp.response?.data;
        if (data?.errors) {
          const mappedErrors: Record<string, string> = {};
          Object.entries(data.errors).forEach(([key, val]) => {
            const field = key.charAt(0).toLowerCase() + key.slice(1);
            mappedErrors[field] = (val as string[])[0];
          });
          setFollowupDateError(mappedErrors.followupDate);
          setNoteError(mappedErrors.note);
          toast.error('Please validate the highlighted fields');
        } else {
          toast.error(data?.message ?? 'Failed to mark reviewer as not ready. Please try again.');
        }
      } else if (resp?.response?.status === 403) {
        toast.error(resp.response?.data?.message ?? 'You do not have permission to perform this action.');
      } else if ((resp?.response?.status ?? 0) >= 500) {
        toast.error('Failed to mark reviewer as not ready. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      dialogVisible={dialogVisible}
      toggleDialog={handleClose}
      className="not-ready-reviewer-dialog"
      title="Mark Reviewer as Not Ready"
      primaryButtonText={isSubmitting ? 'Marking as Not Ready...' : 'Mark as Not Ready'}
      primaryButtonEnabled={!isSubmitting && followupDate.trim().length > 0 && note.trim().length > 0}
      OnPrimaryButtonClick={handleConfirm}
      secondaryButtonText="Cancel"
      OnSecondaryButtonClick={handleClose}
    >
      <p className="not-ready-reviewer-dialog__description">
        Please provide a follow-up date and further details about why the reviewer is not ready to become an active reviewer.
      </p>
      <DateTimePickerInput
        label="Follow-up Date"
        required
        dateOnly
        // Forces the calendar popup to portal into document.body instead of rendering inline —
        // the dialog body scrolls (overflow-y: auto), which otherwise clips the popup.
        isTable
        minDate={new Date()}
        value={followupDate}
        onChange={(value: string) => {
          setFollowupDate(value);
          setFollowupDateError(undefined);
        }}
        error={followupDateError}
      />
      <TextArea
        label="Note"
        required
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
          setNoteError(undefined);
        }}
        error={noteError}
        rows={5}
      />
    </ConfirmDialog>
  );
};

export default ReviewerProfileNotReadyDialog;
